import {useCallback, useEffect, useMemo, useRef, useState, type FormEvent} from "react";
import {useNavigate} from "react-router-dom";
import {createPortal} from "react-dom";
import {paths} from "@/lib/paths";
import {
  addRecentSearch,
  categoryFilterKey,
  getRecentSearches,
  listProductsInfinite,
  searchRich,
  type Product,
  type ProductListParams,
  type SearchCategoryFilter,
  type SearchCategoryHit,
  type SearchRichResponse
} from "@corporatesclub/shared";
import {useI18n} from "./I18nProvider";
import {ProductCard} from "./ProductCard";
import {ProductFilters, type FilterChange} from "./ProductFilters";
import {ProductGridSkeleton} from "./ShopSkeleton";

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 150;

type Props = {
  open: boolean;
  query: string;
  onQueryChange: (q: string) => void;
  onClose: () => void;
};

type RailItem = {id: number; nume: string; url: string};
type PendingCatNav = string;

function buildSearchPageUrl(
  trimmed: string,
  catFilter: SearchCategoryFilter | null,
  filters: Record<string, number[]>,
  pretMin?: number,
  pretMax?: number
) {
  const q = new URLSearchParams();
  q.set("s", trimmed);
  if(catFilter?.c) q.set("c", catFilter.c);
  if(catFilter?.sub) q.set("sub", catFilter.sub);
  if(catFilter?.subsub) q.set("subsub", catFilter.subsub);
  if(catFilter?.subsubsub) q.set("subsubsub", catFilter.subsubsub);
  if(pretMin !== undefined) q.set("pretMin", String(pretMin));
  if(pretMax !== undefined) q.set("pretMax", String(pretMax));
  Object.entries(filters).forEach(([fid, vals]) => vals.forEach((v) => q.append(`${fid}[]`, String(v))));
  return `${paths.search}?${q.toString()}`;
}

export function HeaderSearchOverlay({open, query, onQueryChange, onClose}: Props) {
  const {t} = useI18n();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const productsRef = useRef<HTMLDivElement>(null);
  const queryRef = useRef(query);
  const [metaLoading, setMetaLoading] = useState(false);
  const [meta, setMeta] = useState<SearchRichResponse | null>(null);
  const [catFilter, setCatFilter] = useState<SearchCategoryFilter | null>(null);
  const [filters, setFilters] = useState<Record<string, number[]>>({});
  const [pretMin, setPretMin] = useState<number | undefined>();
  const [pretMax, setPretMax] = useState<number | undefined>();
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [pendingCatNav, setPendingCatNav] = useState<PendingCatNav | null>(null);
  const metaTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const productsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trimmed = query.trim();
  const hasQuery = trimmed.length >= 2;
  const listParams = useMemo<ProductListParams>(() => ({
    s: trimmed,
    ...(catFilter?.c ? catFilter : {})
  }), [trimmed, catFilter]);
  const fetchParams = useMemo<ProductListParams>(() => ({
    s: trimmed,
    ...(catFilter?.c ? catFilter : {}),
    filters,
    pretMin,
    pretMax,
    pageSize: PAGE_SIZE
  }), [trimmed, catFilter, filters, pretMin, pretMax]);
  const filterScopeKey = `${trimmed}-${categoryFilterKey(catFilter)}`;
  const loadProducts = useCallback(async(pageNum: number, append: boolean) => {
    if(!hasQuery) return;
    if(append) setLoadingMore(true);
    else setLoading(true);
    try {
      const res = await listProductsInfinite({...fetchParams, page: pageNum});
      setProducts((prev) => append ? [...prev, ...res.items] : res.items);
      setTotal(res.total);
      setPage(res.page);
      setHasMore(res.hasMore ?? res.page < res.totalPages);
    } catch {
      if(!append) {
        setProducts([]);
        setTotal(0);
        setHasMore(false);
      }
    } finally {
      if(append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [fetchParams, hasQuery]);
  useEffect(() => { queryRef.current = query; }, [query]);
  useEffect(() => {
    if(!open) return;
    setRecentSearches(getRecentSearches());
  }, [open]);
  useEffect(() => {
    if(!open) return;
    inputRef.current?.focus();
    document.body.classList.add("cc-searchOpen");
    function onKey(e: KeyboardEvent) {
      if(e.key === "Escape") {
        if(pendingCatNav) setPendingCatNav(null);
        else onClose();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("cc-searchOpen");
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, pendingCatNav]);
  useEffect(() => {
    if(!open) {
      setMeta(null);
      setProducts([]);
      setTotal(0);
      setPage(1);
      setHasMore(false);
      setFilters({});
      setCatFilter(null);
      setPretMin(undefined);
      setPretMax(undefined);
      setPendingCatNav(null);
      return;
    }
    setFilters({});
    setCatFilter(null);
    setPretMin(undefined);
    setPretMax(undefined);
  }, [open, trimmed]);
  useEffect(() => {
    if(!open || !hasQuery) {
      setMeta(null);
      setMetaLoading(false);
      return;
    }
    if(metaTimer.current) clearTimeout(metaTimer.current);
    setMeta(null);
    setMetaLoading(true);
    metaTimer.current = setTimeout(() => {
      const q = trimmed;
      searchRich(q).then((data) => {
        if(queryRef.current.trim() === q) setMeta(data);
      }).catch(() => {
        if(queryRef.current.trim() === q) setMeta(null);
      }).finally(() => {
        if(queryRef.current.trim() === q) setMetaLoading(false);
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => { if(metaTimer.current) clearTimeout(metaTimer.current); };
  }, [open, hasQuery, trimmed]);
  useEffect(() => {
    if(!open || !hasQuery) {
      setProducts([]);
      setTotal(0);
      setPage(1);
      setHasMore(false);
      setLoading(false);
      return;
    }
    setProducts([]);
    setTotal(0);
    setHasMore(false);
    setLoading(true);
    if(productsTimer.current) clearTimeout(productsTimer.current);
    productsTimer.current = setTimeout(() => {
      loadProducts(1, false);
    }, SEARCH_DEBOUNCE_MS);
    return () => { if(productsTimer.current) clearTimeout(productsTimer.current); };
  }, [open, hasQuery, loadProducts]);
  function onFiltersChange(next: FilterChange) {
    setFilters(next.filters);
    setPretMin(next.pretMin);
    setPretMax(next.pretMax);
  }
  function scrollRail(dir: -1 | 1) {
    railRef.current?.scrollBy({left: dir * 280, behavior: "smooth"});
  }
  function scrollProductsTop() {
    productsRef.current?.scrollTo({top: 0, behavior: "smooth"});
  }
  function loadMore() {
    if(loadingMore || !hasMore) return;
    loadProducts(page + 1, true);
  }
  function rememberSearch(term: string) {
    setRecentSearches(addRecentSearch(term));
  }
  function pickRecent(term: string) {
    onQueryChange(term);
    inputRef.current?.focus();
  }
  function submitSearch(e?: FormEvent) {
    e?.preventDefault();
    const s = query.trim();
    if(s.length < 2) return;
    rememberSearch(s);
    inputRef.current?.focus();
  }
  function askOpenCategory(url: string) {
    setPendingCatNav(url);
  }
  function confirmOpenCategory() {
    if(!pendingCatNav) return;
    onClose();
    navigate(pendingCatNav);
    setPendingCatNav(null);
  }
  function viewAllResults() {
    rememberSearch(trimmed);
    onClose();
    navigate(buildSearchPageUrl(trimmed, catFilter, filters, pretMin, pretMax));
  }
  if(!open) return null;
  const searchCategories = meta?.categories ?? [];
  const categoryFacets = meta?.categoryFacets ?? [];
  const railItems: RailItem[] = searchCategories.map((c: SearchCategoryHit) => ({id: c.id, nume: c.nume, url: c.url}));
  const showRail = hasQuery && (metaLoading || searchCategories.length > 0);
  return createPortal(
    <>
      <div className="cc-searchOverlay isOpen" role="dialog" aria-modal="true" aria-label={t("Search for products...")}>
        <button type="button" className="cc-searchOverlay__bg" aria-label={t("Close search")} onClick={onClose} />
        <div className="cc-searchOverlay__limit">
          <div className="cc-searchOverlay__inner">
            <form className="cc-searchOverlay__searchbar" onSubmit={submitSearch}>
              <div className="cc-searchOverlay__bar">
                <input
                  ref={inputRef}
                  type="text"
                  className="cc-searchOverlay__input"
                  value={query}
                  onChange={(e) => onQueryChange(e.target.value)}
                  placeholder={t("Search for products...")}
                  autoComplete="off"
                />
                <button type="button" className="cc-searchOverlay__close" onClick={onClose} aria-label={t("Close search")}>
                  <i className="fa fa-times" aria-hidden="true" />
                </button>
              </div>
            </form>
            {showRail ? (
              <div className="cc-searchOverlay__railWrap">
                <button type="button" className="cc-searchOverlay__railNav cc-searchOverlay__railNav--prev" aria-label={t("Previous")} onClick={() => scrollRail(-1)}>
                  <i className="fa fa-chevron-left" aria-hidden="true" />
                </button>
                <div className="cc-searchOverlay__rail" ref={railRef}>
                  {hasQuery && metaLoading ? (
                    Array.from({length: 6}, (_, i) => (
                      <span key={i} className="cc-searchOverlay__badgeSk" aria-hidden="true" />
                    ))
                  ) : (
                    railItems.map((item) => (
                      <button key={`${item.id}-${item.url}`} type="button" className="cc-searchOverlay__badge" onClick={() => askOpenCategory(item.url)}>
                        <i className="fa fa-th-large" aria-hidden="true" />
                        <span>{item.nume}</span>
                      </button>
                    ))
                  )}
                </div>
                <button type="button" className="cc-searchOverlay__railNav cc-searchOverlay__railNav--next" aria-label={t("Next")} onClick={() => scrollRail(1)}>
                  <i className="fa fa-chevron-right" aria-hidden="true" />
                </button>
              </div>
            ) : null}
            {!hasQuery ? (
              <div className="cc-searchOverlay__idle">
                <p className="cc-searchOverlay__idleHint">{t("Start typing to search")}</p>
                {recentSearches.length ? (
                  <div className="cc-searchOverlay__recent">
                    <h3 className="cc-searchOverlay__recentTitle">{t("Recent searches")}</h3>
                    <ul className="cc-searchOverlay__recentList">
                      {recentSearches.map((term) => (
                        <li key={term}>
                          <button type="button" className="cc-searchOverlay__recentBtn" onClick={() => pickRecent(term)}>
                            <i className="fa fa-history" aria-hidden="true" />
                            <span>{term}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : (
            <div className="cc-searchOverlay__split">
              <aside className="cc-searchOverlay__filters">
                <ProductFilters
                  key={filterScopeKey}
                  params={listParams}
                  filters={filters}
                  pretMin={pretMin}
                  pretMax={pretMax}
                  showCategories={false}
                  resetWhileLoading
                  categoryFacets={categoryFacets}
                  categoryFilter={catFilter}
                  onCategoryChange={setCatFilter}
                  categoryFacetsLoading={metaLoading}
                  onChange={onFiltersChange}
                />
              </aside>
              <div className="cc-searchOverlay__products" ref={productsRef}>
                {loading && !products.length ? (
                  <>
                    <div className="cc-searchOverlay__mainHead">
                      <h3>{t("Search results")}</h3>
                    </div>
                    <ProductGridSkeleton count={8} />
                  </>
                ) : (
                  <>
                    <div className="cc-searchOverlay__mainHead">
                      <h3>{t("Search results")}{total > 0 ? ` (${total})` : ""}</h3>
                      {total > 0 ? (
                        <button type="button" className="cc-searchOverlay__viewAll" onClick={viewAllResults}>
                          {t("View all results")}
                        </button>
                      ) : null}
                    </div>
                    {products.length ? (
                      <div className="cc-productGrid cc-productGrid--overlay">
                        {products.map((p) => (
                          <ProductCard key={p.id} product={p} compact onNavigate={onClose} />
                        ))}
                      </div>
                    ) : !loading ? (
                      <p className="cc-searchOverlay__empty">{t("No products found")}</p>
                    ) : null}
                    {hasMore ? (
                      <div className="cc-searchOverlay__moreWrap">
                        <button type="button" className="cc-searchOverlay__loadMore" onClick={loadMore} disabled={loadingMore}>
                          {loadingMore ? t("Loading products...") : t("Load more")}
                        </button>
                      </div>
                    ) : null}
                  </>
                )}
                {hasQuery && products.length > 0 ? (
                  <button type="button" className="cc-searchOverlay__toTop" onClick={scrollProductsTop} aria-label={t("Back to top")}>
                    <i className="fa fa-chevron-up" aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
      {pendingCatNav ? (
        <div className="cc-searchOverlay__confirm" role="alertdialog" aria-modal="true" aria-labelledby="cc-searchCatConfirmTitle">
          <div className="cc-searchOverlay__confirmBox">
            <h4 id="cc-searchCatConfirmTitle" className="cc-searchOverlay__confirmTitle">{t("Leave search for category?")}</h4>
            <p className="cc-searchOverlay__confirmText">{t("You are about to leave search and open the category page. Continue?")}</p>
            <div className="cc-searchOverlay__confirmActions">
              <button type="button" className="cc-searchOverlay__confirmBtn" onClick={() => setPendingCatNav(null)}>{t("Cancel")}</button>
              <button type="button" className="cc-searchOverlay__confirmBtn cc-searchOverlay__confirmBtn--primary" onClick={confirmOpenCategory}>{t("Open category page")}</button>
            </div>
          </div>
        </div>
      ) : null}
    </>,
    document.body
  );
}
